import { Icon } from '@eventbrite/eds-icon';
import { HandWithMoneyGreen } from '@eventbrite/eds-iconography';
import { Illustration } from '@eventbrite/eds-illustration';
import { Modal } from '@eventbrite/eds-modal';
import React, { useEffect, useState } from 'react';
import {
    missingInformationString,
    ModalWindowActions,
    PayoutMissingInformation,
} from '../types';
import { getSessionStorageOrDefault } from '../utils';
import './DocumentRequestModal.scss';
import { getTaxpayerInformation } from './hooks';

const STATE_KEY = 'document_request_status_modal_seen';

const actionsList = (actions: ModalWindowActions[]) => {
    const renderActionsList = actions.map(
        (itemAction: ModalWindowActions, i: number) => {
            const ItemIcon = itemAction.icon;
            const text = itemAction.text;

            return (
                <div
                    key={`icon-${i}`}
                    className="add-bank-account eds-l-mar-bot-3"
                >
                    <Icon type={<ItemIcon />} />
                    <div className="bank-account-option-text eds-l-mar-left-3">
                        {text}
                    </div>
                </div>
            );
        },
    );

    return <div className="actions-list">{renderActionsList}</div>;
};

const getSubtitleHTML = (
    isMismatchedStatusBody: boolean,
    subTitle: missingInformationString,
) => {
    if (isMismatchedStatusBody) {
        return (
            <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: subTitle as string,
                }}
            ></div>
        );
    }

    return <p className="eds-l-mar-bot-3">{subTitle}</p>;
};

export const DocumentRequestModal = ({
    onRedirect,
}: {
    onRedirect: () => void;
}) => {
    const orgId = window?.__SERVER_DATA__?.activeOrganizationId || '';
    const keyModalSeen = `${orgId}_${STATE_KEY}`;
    const missingInformationObject = getTaxpayerInformation(orgId);

    const [isShown, setIsShown] = useState(false);
    const [title, setTitle] = useState<missingInformationString>('');
    const [subTitle, setSubTitle] = useState<missingInformationString>('');
    const [buttonText, setButtonText] = useState<missingInformationString>('');
    const [isMismatchedStatusBody, setIsMismatchedStatusBody] = useState(false);
    const [buttonLink, setButtonLink] = useState<string | undefined>('');
    const [modalSeen, setModalSeen] = useState(
        getSessionStorageOrDefault(keyModalSeen, false),
    );

    const subTitleHTML = getSubtitleHTML(isMismatchedStatusBody, subTitle);

    useEffect(() => {
        const payoutMissingInformationObject =
            missingInformationObject as PayoutMissingInformation;

        const { modalNotification, message } =
            payoutMissingInformationObject || {};

        const hastoShowModalNotification =
            !modalSeen && missingInformationObject && modalNotification;

        if (hastoShowModalNotification) {
            setTitle(message?.title);
            if (
                typeof message?.subTitle === 'string' ||
                message?.subTitle instanceof String
            ) {
                setSubTitle(message?.subTitle);
            } else {
                setSubTitle(message?.subTitle?.__html);
                setIsMismatchedStatusBody(true);
            }
            setButtonText(message?.buttonText);
            setButtonLink(message?.buttonLink);
            setIsShown(true);
        } else {
            setIsShown(false);
        }
    }, [
        orgId,
        missingInformationObject,
        title,
        subTitle,
        buttonLink,
        modalSeen,
    ]);

    useEffect(() => {
        sessionStorage.setItem(keyModalSeen, JSON.stringify(modalSeen));
    }, [modalSeen, keyModalSeen]);

    const closeModal = () => {
        setIsShown(false);
        setModalSeen(true);
    };

    const redirectOnClick = () => {
        closeModal();
        onRedirect && onRedirect();
        window.location.href = buttonLink as string;
    };

    const renderOptions = () => {
        const actions =
            (missingInformationObject as PayoutMissingInformation)?.actions ||
            [];

        if (!actions.length) return null;

        return actionsList(actions);
    };

    const illustrationContent = (
        <section>
            <h2 className="eds-text-hs--fixed eds-l-mar-bot-3">{title}</h2>
            {subTitleHTML}
            {renderOptions()}
        </section>
    );

    return (
        <Modal
            isShown={isShown}
            isPortal={true}
            noMinHeight={true}
            isClosable={true}
            disableCloseOnClickOutside={true}
            onClose={closeModal}
            primaryText={buttonText}
            primaryType="fill"
            onPrimaryAction={() => {
                redirectOnClick();
            }}
            illustration={
                <Illustration
                    type={<HandWithMoneyGreen />}
                    height="350px"
                    width="auto"
                />
            }
            __containerClassName="document-request-modal"
        >
            {illustrationContent}
        </Modal>
    );
};

export default DocumentRequestModal;
